import React from 'react';
import useCountUp from '../hooks/useCountUp';
import { useTheme } from '../lib/themeContext';

interface Props {
  score: number;
  label: string;
  size?: number;
  strokeWidth?: number;
  color?: string;
  /** Compact mode for inline use (44px, no label) */
  compact?: boolean;
}

function scoreColor(score: number): string {
  if (score >= 75) return '#00A896';   // teal - excellent
  if (score >= 55) return '#1E6FBF';   // blue - good
  if (score >= 35) return '#F59E0B';   // amber - fair
  return '#EF4444';                    // red - needs attention
}

export default function ScoreRing({
  score,
  label,
  size: sizeProp,
  strokeWidth: swProp,
  color,
  compact = false,
}: Props) {
  const size = sizeProp ?? (compact ? 44 : 88);
  const strokeWidth = swProp ?? (compact ? 4 : 7);
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const ringColor = color || scoreColor(score);
  const animatedScore = useCountUp(score > 0 ? score : 0);
  const { isDark } = useTheme();

  return (
    <div className={compact ? 'flex items-center justify-center' : 'flex flex-col items-center gap-1'}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* Track */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none" stroke={isDark ? '#4B5563' : '#E5E7EB'} strokeWidth={strokeWidth}
          />
          {/* Progress */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            stroke={ringColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - pct)}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        {/* Score number */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <span className="font-bold text-arete-navy dark:text-gray-100" style={{ fontSize: size * 0.26, lineHeight: 1 }}>
            {score > 0 ? animatedScore : '–'}
          </span>
        </div>
      </div>
      {!compact && (
        <span className="text-xs font-medium text-arete-muted text-center" style={{ maxWidth: size }}>
          {label}
        </span>
      )}
    </div>
  );
}
